import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Lock, Check, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [formData, setFormData] = useState({ password: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [validToken, setValidToken] = useState(null);

  useEffect(() => {
    if (!token) {
      setValidToken(false);
      return;
    }
    // Verify token on mount
    const verifyToken = async () => {
      try {
        await axios.get(`${API}/player/verify-reset-token?token=${token}`);
        setValidToken(true);
      } catch (err) {
        setValidToken(false);
      }
    };
    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/player/reset-password`, {
        token,
        new_password: formData.password
      });
      setSuccess(true);
      setTimeout(() => navigate('/player/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error resetting password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (validToken === null) {
    return (
      <div className="page-content">
        <section className="page-hero" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <p>Verifying reset link...</p>
        </section>
      </div>
    );
  }

  if (validToken === false) {
    return (
      <div className="page-content">
        <section className="page-hero" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center' }}>
          <div className="container" style={{ maxWidth: '480px' }}>
            <div className="network-card" style={{ padding: '48px 32px', textAlign: 'center' }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'rgba(220, 38, 38, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px'
              }}>
                <X size={40} style={{ color: '#dc2626' }} />
              </div>
              <h1 className="heading-1" style={{ marginBottom: '16px' }}>Invalid or Expired Link</h1>
              <p style={{ color: 'var(--text-light)', marginBottom: '24px' }}>
                This password reset link is invalid or has expired. Please request a new one.
              </p>
              <Link to="/player/forgot-password" className="btn-primary">
                Request New Link
              </Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="page-content">
      <section className="page-hero" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center' }}>
        <div className="container" style={{ maxWidth: '480px' }}>
          <div className="network-card" style={{ padding: '48px 32px' }}>
            {success ? (
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: 'rgba(34, 197, 94, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px'
                }}>
                  <Check size={40} style={{ color: '#22c55e' }} />
                </div>
                <h1 className="heading-1" style={{ marginBottom: '16px' }}>Password Reset!</h1>
                <p style={{ color: 'var(--text-light)', marginBottom: '24px' }}>
                  Your password has been successfully reset. Redirecting to login...
                </p>
              </div>
            ) : (
              <>
                <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                  <h1 className="heading-1" style={{ marginBottom: '8px' }}>Reset Password</h1>
                  <p className="body-medium" style={{ color: 'var(--text-light)' }}>
                    Enter your new password below
                  </p>
                </div>

                {error && (
                  <div style={{
                    padding: '12px 16px',
                    background: 'rgba(220, 38, 38, 0.2)',
                    border: '1px solid #dc2626',
                    borderRadius: '8px',
                    color: '#ef4444',
                    marginBottom: '24px',
                    fontSize: '0.875rem'
                  }}>
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  <div className="form-group" style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <Lock size={16} /> New Password
                    </label>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                      required
                      placeholder="Enter new password (min 6 characters)"
                    />
                  </div>

                  <div className="form-group" style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <Lock size={16} /> Confirm New Password
                    </label>
                    <input
                      type="password"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      required
                      placeholder="Confirm new password"
                    />
                  </div>

                  <button 
                    type="submit" 
                    className="btn-primary" 
                    disabled={loading}
                    style={{ width: '100%' }}
                  >
                    {loading ? 'Resetting...' : 'Reset Password'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ResetPassword;
